import { useEffect, useRef } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Underline as UnderlineIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { ptToPx, type ResolvedTypography } from "@/lib/stylesheet-typography";

// A TipTap rich-text block used as a slide content window (TODO §2). ProseMirror under
// the hood, same as Gridd cells, and — like the Gridd embed — treated as *uncontrolled
// after mount*: `json` seeds the editor once, edits flow out (debounced), and incoming
// `json` prop changes are never fed back in (that would reset the cursor). Switching a
// slide's content type unmounts/remounts this, which re-seeds cleanly.

const DEBOUNCE_MS = 300; // TODO §2.3 — don't dispatch a store write per keystroke.

export function TextBlock({
  json,
  onChange,
  readOnly,
  typography,
}: {
  json: unknown; // ProseMirror doc
  onChange: (json: unknown) => void;
  readOnly: boolean;
  typography: ResolvedTypography;
}) {
  // Keep the latest onChange without re-creating the editor when the parent re-renders.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Debounce state: the pending doc and its timer, flushed on unmount so a fast edit
  // right before switching slides isn't lost.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<unknown>(null);

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      // StarterKit (v3) bundles bold/italic/underline/lists/headings; TextAlign is the
      // one extra we need. types match Gridd cells' textAlign so the two text models
      // stay visually consistent (TODO §2.1).
      StarterKit,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: json ?? { type: "doc", content: [{ type: "paragraph" }] },
    editorProps: {
      attributes: { class: "slide-text-editor focus:outline-none" },
    },
    onUpdate: ({ editor }) => {
      pending.current = editor.getJSON();
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        const doc = pending.current;
        pending.current = null;
        if (doc !== null) onChangeRef.current(doc);
      }, DEBOUNCE_MS);
    },
  });

  // readOnly honored the same way every control is (TODO §2.5).
  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Flush any pending debounced edit on unmount.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        if (pending.current !== null) onChangeRef.current(pending.current);
      }
    },
    [],
  );

  // Subscribe narrowly to the flags the toolbar reflects (v3 pattern) so the buttons
  // stay in sync with the selection without re-rendering on every transaction.
  const active = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor?.isActive("bold") ?? false,
      italic: editor?.isActive("italic") ?? false,
      underline: editor?.isActive("underline") ?? false,
      bullet: editor?.isActive("bulletList") ?? false,
      ordered: editor?.isActive("orderedList") ?? false,
      left: editor?.isActive({ textAlign: "left" }) ?? false,
      center: editor?.isActive({ textAlign: "center" }) ?? false,
      right: editor?.isActive({ textAlign: "right" }) ?? false,
    }),
  });

  if (!editor) return null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-md border border-slate-200">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1">
          <ToolbarButton
            active={active?.bold}
            label="Bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            active={active?.italic}
            label="Italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            active={active?.underline}
            label="Underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-4 w-4" />
          </ToolbarButton>
          <Divider />
          <ToolbarButton
            active={active?.bullet}
            label="Bullet list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            active={active?.ordered}
            label="Numbered list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <Divider />
          <ToolbarButton
            active={active?.left}
            label="Align left"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <AlignLeft className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            active={active?.center}
            label="Align center"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <AlignCenter className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            active={active?.right}
            label="Align right"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <AlignRight className="h-4 w-4" />
          </ToolbarButton>
        </div>
      )}
      {/* Base typography resolves against the deck stylesheet (TODO §2.4); marks/nodes
          override it. Authored in points (§2.6), converted to px for this scaled canvas. */}
      <div
        className="min-h-0 flex-1 overflow-auto p-4"
        style={{
          fontFamily: typography.fontFamily,
          fontSize: ptToPx(typography.fontSizePt),
          color: typography.color,
          fontWeight: typography.fontWeight,
          fontStyle: typography.fontStyle,
        }}
      >
        <EditorContent editor={editor} className="h-full" />
      </div>
    </div>
  );
}

function ToolbarButton({
  active,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      // Keep the editor selection while clicking the toolbar.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-200",
        active && "bg-slate-900 text-white hover:bg-slate-900",
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-slate-300" />;
}
